
import Vue from 'vue';
import { db } from '@/firebase';
import { RunnitNodeDef, RunnitNodeRun, RunnitNodeRunResult } from '@run-diffusion/shared';
import _shuffle from 'lodash/shuffle';
import _unionBy from 'lodash/unionBy';
import { ROUTER } from '@/router/constants';
import { mapState } from 'vuex';
import { RunnitRemixMixin } from '@/mixins';
import RunnitResult from '@/views/Runnits/RunnitResult.vue';
import BaseButton from '@/components/base/BaseButton.vue';

export default Vue.extend({
	name: 'FeaturedNodeDefResultsContainer',
	mixins: [
		RunnitRemixMixin,
	],
	props: {
		nodeDefId: {
			type: String,
			required: true,
		},
		darkBackground: {
			type: Boolean,
			default: false,
		},
		onlyResults: {
			type: Boolean,
			default: false,
		},
		noClick: {
			type: Boolean,
			default: false,
		},
		toolOnboardingTopCardVisible: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			remixPlaceholderNodeRunsById: {} as Record<string, RunnitNodeRun>,
			remixPlaceholderShuffleCounter: 0 as number,
			visibleRemixPlaceholderResults: [] as RunnitNodeRunResult[],
			nodeDef: null as RunnitNodeDef | null,
			lastDoc: null as any,
			isLoadingMore: false,
			hasMoreResults: true,
			allFeaturedResults: [] as RunnitNodeRunResult[],
			pageSize: 3,
		};
	},
	computed: {
		...mapState(['user']),
	},
	watch: {
		remixPlaceholderShuffleCounter: {
			immediate: true,
			handler () {
				this.pickRandomFeaturedResults();
			},
		},
		remixPlaceholderNodeRunsById: {
			immediate: true,
			handler () {
				if (!this.visibleRemixPlaceholderResults.length) {
					this.pickRandomFeaturedResults();
				}
			},
		},
	},
	async created () {
		await this.initFeaturedNodeRuns();
	},
	methods: {
		async initFeaturedNodeRuns () {
			try {
				// Create base query for featured runs
				let nodeRunsQuery = db.collection('runnitNodeRuns')
					.where('isFeaturedNodeDef', '==', true)
					.where('nodeDefId', '==', this.nodeDefId)
					.limit(this.pageSize + 1);

				// Execute query and get results
				const snapshot = await nodeRunsQuery.get();
				this.hasMoreResults = snapshot.docs.length === this.pageSize + 1;
				if (snapshot.docs.length) {
					this.$emit('has-results', true);
					this.lastDoc = this.hasMoreResults ? snapshot.docs[snapshot.docs.length - 2] : snapshot.docs[snapshot.docs.length - 1];
				} else {
					this.$emit('has-results', false);
					this.lastDoc = null;
				}

				// Map the documents to their data and save to remixPlaceholderNodeRuns
				const remixPlaceholderNodeRuns = snapshot.docs.map(doc => ({
					id: doc.id,
					...doc.data()
				})) as RunnitNodeRun[];
				if (this.hasMoreResults) {
					remixPlaceholderNodeRuns.pop(); // Remove the last one because it's the extra one
				}

				this.remixPlaceholderNodeRunsById = remixPlaceholderNodeRuns.reduce((acc: Record<string, RunnitNodeRun>, run: RunnitNodeRun) => {
					acc[run.id] = run;
					return acc;
				}, {} as Record<string, RunnitNodeRun>);

				// Process initial results
				this.processNewResults(remixPlaceholderNodeRuns);

				// Fetch the nodeDef
				const nodeDefDoc = await db.doc(`runnitNodeDefs/${this.nodeDefId}`).get();
				this.nodeDef = { id: nodeDefDoc.id, ...nodeDefDoc.data() } as RunnitNodeDef;

			} catch (error) {
				console.error('Error fetching featured images', error);
			}
		},

		async loadMoreResults () {
			if (!this.hasMoreResults || this.isLoadingMore || !this.lastDoc) return;

			try {
				this.isLoadingMore = true;
				let query = db.collection('runnitNodeRuns')
					.where('isFeaturedNodeDef', '==', true)
					.where('nodeDefId', '==', this.nodeDefId)
					.startAfter(this.lastDoc)
					.limit(this.pageSize + 1);

				const snapshot = await query.get();
				this.hasMoreResults = snapshot.docs.length === this.pageSize + 1;
				if (snapshot.docs.length) {
					this.lastDoc = this.hasMoreResults ? snapshot.docs[snapshot.docs.length - 2] : snapshot.docs[snapshot.docs.length - 1];
				} else {
					this.lastDoc = null;
				}

				const newNodeRuns = snapshot.docs.map(doc => ({
					id: doc.id,
					...doc.data()
				})) as RunnitNodeRun[];

				if (this.hasMoreResults) {
					newNodeRuns.pop(); // Remove the last one because it's only to see if there are more results
				}

				// Update the nodeRuns map
				newNodeRuns.forEach(run => {
					this.remixPlaceholderNodeRunsById[run.id] = run;
				});

				// Process new results
				this.processNewResults(newNodeRuns);
			} catch (error) {
				console.error('Error loading more results', error);
			} finally {
				this.isLoadingMore = false;
			}
		},

		processNewResults (nodeRuns: RunnitNodeRun[]) {
			const newResults = nodeRuns.reduce((acc: RunnitNodeRunResult[], run: RunnitNodeRun) => {
				const filteredResults = run.results.filter((result: RunnitNodeRunResult) =>
					result.isFeaturedNodeDef && !result.deletedAt
				);
				return _unionBy(acc, filteredResults, 'uuid');
			}, []);

			this.allFeaturedResults = _unionBy(this.allFeaturedResults, newResults, 'uuid');
			this.pickRandomFeaturedResults();
		},

		pickRandomFeaturedResults () {
			if (this.hasMoreResults) {
				this.visibleRemixPlaceholderResults = this.allFeaturedResults.slice(-3);
			} else {
				this.visibleRemixPlaceholderResults = _shuffle([...this.allFeaturedResults]).slice(0, 3);
			}
		},

		handleRemixClick (nodeRunId: string, resultUuid: string) {
			if (!this.user) {
				const currentPath: string = this.$router.resolve({
					name: ROUTER.RUNNIT_REMIX,
					query: {
						rnrid: nodeRunId,
						ruuid: resultUuid,
						autoRemix: true,
					},
				}).href;
				this.routerPush(this.$route, this.$router, {
					name: ROUTER.LOGIN,
					query: { redirect: currentPath },
				});
			} else {
				const nodeRun = this.remixPlaceholderNodeRunsById[nodeRunId];
				this.openToolWithSameInputs(this.nodeDef, nodeRun, nodeRunId, nodeRun ? nodeRun.runnitId : null, nodeRun ? nodeRun.nodeId : null);
			}
		},
	},
	components: {
		RunnitResult,
		BaseButton,
	},
});
